import PropTypes from "prop-types";
import React, { useContext } from "react";

import { fireTrack } from "@swa-ui/analytics";
import { useDeviceInfo } from "@swa-ui/browser";
import { AriaLive, Dialog, Heading, useDialog } from "@swa-ui/core";
import i18n from "@swa-ui/locale";
import { applyOverrides, CallToAction, HtmlValue, PlacementsContext } from "@swa-ui/placements";

import styles from "./RotatingBannerPlacement.module.scss";

export const RotatingBannerPlacement = (props) => {
  const { placement, placementData = {} } = props;
  const { callToAction, dialog, graphic, text } = placement ?? {};
  const { placementsConfig = {} } = useContext(PlacementsContext);
  const { dialogRef, hideDialog, isDialogVisible, showDialog } = useDialog("center");
  const { screenSize } = useDeviceInfo();

  return isChasePrequal() ? renderWithChasePrequalWrapper() : renderRotatingBanner();

  function renderRotatingBanner() {
    return (
      <div className={styles.rotatingBannerContainer}>
        {isDialogVisible && dialog ? (
          <Dialog {...getDialogProps()}>{renderDialogContent()}</Dialog>
        ) : null}
        {graphic?.imagePath && renderGraphic()}
        <AriaLive hiddenFromScreen={false} ariaLiveSetting="assertive">
          <div className={styles.rotatingBannerContentContainer}>
            {text?.value && renderTextContent(text)}
            {callToAction && <CallToAction {...getCallToActionProps()} />}
          </div>
        </AriaLive>
      </div>
    );
  }

  function renderWithChasePrequalWrapper() {
    const ChasePrequalWrapper = placementsConfig.configuredCommands.CHASE_PREQUAL;

    return (
      <ChasePrequalWrapper placementData={placementData}>
        {renderRotatingBanner()}
      </ChasePrequalWrapper>
    );
  }

  function renderGraphic() {
    const { altText, display, imagePath } = applyOverrides(graphic, screenSize);

    return display !== "none" ? (
      <img alt={altText} className={styles.rotatingBannerImage} src={imagePath} />
    ) : null;
  }

  function renderTextContent(BannerText) {
    const { value } = applyOverrides(BannerText, screenSize);

    return <HtmlValue htmlValue={value} />;
  }

  function renderDialogContent() {
    return (
      <>
        {dialog?.title?.value && <Heading {...getHeadingProps(dialog?.title)} />}
        {dialog?.text?.value && renderTextContent(dialog?.text)}
        {dialog?.callToAction && (
          <CallToAction {...getDialogCallToActionProps(dialog.callToAction)} />
        )}
      </>
    );
  }

  function getCallToActionProps() {
    return {
      ...callToAction,
      ...(dialog && {
        additionalProps: {
          onClick: handleDialogChange,
        },
      }),
    };
  }

  function getDialogProps() {
    return {
      "aria-label-close": i18n("RotatingBannerPlacement__ARIA_DIALOG_MODAL_CLOSE"),
      "aria-live": i18n("RotatingBannerPlacement__ARIA_DIALOG_MODAL_OPENED"),
      buttonGroupProps: {
        "aria-label-next": i18n("RotatingBannerPlacement__BUTTON_GROUP_ARIA_NEXT"),
        "aria-label-previous": i18n("RotatingBannerPlacement__BUTTON_GROUP_ARIA_PREVIOUS"),
      },
      dialogRef,
      onClose: handleClose,
      revealed: isDialogVisible,
      showClose: true,
    };
  }

  function getDialogCallToActionProps(dialogCallToAction) {
    return {
      ...dialogCallToAction,
      additionalProps: {
        fullWidth: dialogCallToAction.type === "button",
        paddingForFocus: false,
      },
      className: styles.dialogCallToAction,
    };
  }

  function getHeadingProps(dialogHeadingTitle) {
    const { color, fontSize, fontWeight, value } = dialogHeadingTitle;

    return {
      children: <span style={{ color, fontSize, fontWeight }}>{value}</span>,
      className: styles.dialogHeading,
      headingLevel: 3,
    };
  }

  function handleClose() {
    hideDialog();
  }

  function handleDialogChange() {
    const { analyticsValue = "" } = placementData;

    if (analyticsValue) {
      fireTrack("squid", { page_description: analyticsValue });
    }

    showDialog();
  }

  function isChasePrequal() {
    return !!(placementData?.isChasePrequal && placementsConfig?.configuredCommands?.CHASE_PREQUAL);
  }
};

RotatingBannerPlacement.displayName = "RotatingBannerPlacement";
RotatingBannerPlacement.propTypes = {
  /** Object that contains placement props, including backgroundColor, callToAction, etc */
  placement: PropTypes.shape({
    /** Object that contains link/button props. */
    callToAction: PropTypes.object,

    /** Object that contains content to show on the dialog. */
    dialog: PropTypes.shape({
      /** Object that contains link/button props. */
      callToAction: PropTypes.object,

      /** Object that contains value and text per screen size. */
      text: PropTypes.shape({
        overrides: PropTypes.shape({
          large: PropTypes.shape({
            value: PropTypes.string,
          }),
          medium: PropTypes.shape({
            value: PropTypes.string,
          }),
          small: PropTypes.shape({
            value: PropTypes.string,
          }),
        }),
        value: PropTypes.string,
      }),

      /** Object that contains title and styles for the heading. */
      title: PropTypes.shape({
        color: PropTypes.string,
        fontSize: PropTypes.string,
        fontWeight: PropTypes.string,
        value: PropTypes.string,
      }),
    }),

    /** Object that contains image props. */
    graphic: PropTypes.shape({
      altText: PropTypes.string,
      imagePath: PropTypes.string,
      overrides: PropTypes.shape({
        large: PropTypes.shape({
          display: PropTypes.string,
          imagePath: PropTypes.string,
        }),
        medium: PropTypes.shape({
          display: PropTypes.string,
          imagePath: PropTypes.string,
        }),
        small: PropTypes.shape({
          display: PropTypes.string,
          imagePath: PropTypes.string,
        }),
      }),
    }),

    /** Object that contains value and override text per screen size. */
    text: PropTypes.shape({
      overrides: PropTypes.shape({
        large: PropTypes.shape({
          value: PropTypes.string,
        }),
        medium: PropTypes.shape({
          value: PropTypes.string,
        }),
        small: PropTypes.shape({
          value: PropTypes.string,
        }),
      }),
      value: PropTypes.string,
    }),
  }),
};
